import { useCallback } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import {
  type FPlusExpressionValue,
  isNotRelevantField,
  isSubTemplateValueSetupField,
  type SubTemplateValueSetupFieldValue,
  type TemplateFieldWithCardinality,
  type TemplateReferenceValueSetupFieldValue,
} from '../../types/templateModel'
import {
  generateEmptyAttributeExpressionValue,
  generateEmptySubTemplateValueSetup,
  generateEmptyTemplateValueSetup,
} from '../../uiModels/createEmptyFieldHelpers'
import { TemplateFieldExpression } from '../templateFieldExpression/TemplateFieldExpression'
import { expressionFromTemplateFieldWithCardinality } from './helpers'

type Props = {
  field: TemplateFieldWithCardinality
}

type FieldWithCardinalityValues =
  | TemplateReferenceValueSetupFieldValue
  | SubTemplateValueSetupFieldValue

export const TemplateFieldCardinality = ({ field }: Props) => {
  const { templateVersion } = useTemplateContext()
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)

  const handleCodeChanged = useCallback(
    (nextValue: FPlusExpressionValue) => {
      const emptyFieldValue = isSubTemplateValueSetupField(field)
        ? generateEmptySubTemplateValueSetup({
            field,
            meta: metaModel,
          })
        : generateEmptyTemplateValueSetup(field)
      const fieldValue = field.value ?? emptyFieldValue
      const nextCardinalityValue = fieldValue.cardinality
        ? fieldValue.cardinality
        : generateEmptyAttributeExpressionValue({
            path: field.path,
            fieldName: 'cardinality',
          })

      const nextFieldValue = {
        ...fieldValue,
        cardinality: {
          ...nextCardinalityValue,
          expression: nextValue,
          origin: nextCardinalityValue.path,
        },
      } satisfies FieldWithCardinalityValues

      return (field.update as (v: FieldWithCardinalityValues) => Promise<void>)(
        nextFieldValue,
      )
    },
    [field, metaModel],
  )

  const fullPath = `${field.path}.cardinality`
  const expression = expressionFromTemplateFieldWithCardinality(field)

  return (
    <TemplateFieldExpression
      expression={expression}
      isExpanded
      onChange={isNotRelevantField(field) ? undefined : handleCodeChanged}
      path={fullPath}
      fallbackCode=""
      fromOrigin={field.value?.cardinality?.origin}
    />
  )
}
